import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { LogOut, Car, DollarSign, Calendar, Wrench, FileText, Printer, ChevronLeft } from 'lucide-react';
import { portalFetch, portalLogout, getPortalToken } from '../lib/portalAuth';

const f$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fDate = (iso) => iso ? new Date(iso).toLocaleDateString() : '—';

const STATUS_STYLE = {
  upcoming: 'bg-blue-100 text-blue-800',
  active: 'bg-emerald-100 text-emerald-800',
  completed: 'bg-gray-100 text-gray-700',
  cancelled: 'bg-red-100 text-red-800',
  pending: 'bg-amber-100 text-amber-800',
  paid: 'bg-emerald-100 text-emerald-800',
  ok: 'bg-green-100 text-green-800',
  due: 'bg-amber-100 text-amber-800',
  overdue: 'bg-red-100 text-red-800',
};

export default function OwnerDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [bookings, setBookings] = useState({ bookings: [], vehicles: [], totals: {} });
  const [payouts, setPayouts] = useState({ payouts: [], totals: {} });
  const [statement, setStatement] = useState(null);
  const [maintenance, setMaintenance] = useState({ schedules: [], recent_logs: [], ytd_spend: 0 });
  const [loading, setLoading] = useState(true);
  const [showStatement, setShowStatement] = useState(false);

  useEffect(() => {
    if (!getPortalToken('owner')) { navigate('/owners/login'); return; }
    Promise.all([
      portalFetch('owner', '/api/portals/owner/me'),
      portalFetch('owner', '/api/portals/owner/bookings'),
      portalFetch('owner', '/api/portals/owner/payouts'),
      portalFetch('owner', '/api/portals/owner/statement'),
      portalFetch('owner', '/api/portals/owner/maintenance'),
    ]).then(([m, b, p, s, mt]) => {
      setMe(m); setBookings(b); setPayouts(p); setStatement(s); setMaintenance(mt);
      setLoading(false);
    }).catch(err => {
      setLoading(false);
      if (err.message.toLowerCase().includes('expired') || err.message.toLowerCase().includes('invalid')) navigate('/owners/login');
    });
  }, [navigate]);

  const logout = async () => { await portalLogout('owner'); navigate('/owners/login'); };

  if (loading) return <Center>Loading…</Center>;
  if (!me) return null;

  return (
    <div className="min-h-screen bg-stone-50" data-testid="owner-dashboard">
      <div className="bg-black text-white">
        <div className="max-w-6xl mx-auto px-5 py-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <Link to="/" className="inline-flex items-center text-[10px] uppercase tracking-widest text-gray-400 hover:text-white mb-1">
              <ChevronLeft className="h-3 w-3 mr-1" /> Back to ACE
            </Link>
            <p className="text-[10px] uppercase tracking-[0.3em] text-red-500 font-bold">ACE · Owner Portal</p>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight mt-1">{me.profile?.owner_name || 'Owner'}</h1>
            <p className="text-xs text-gray-400 mt-1">{me.session.email} · {me.profile?.revenue_share_percent || 70}% revenue share</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/collective" data-testid="owner-collective-link">
              <Button variant="outline" size="sm" className="border-gray-700 text-gray-300 hover:bg-gray-800">
                <Car className="h-3 w-3 mr-1" /> Driven Collective
              </Button>
            </Link>
            <Button onClick={logout} variant="outline" size="sm" className="border-gray-700 text-gray-300 hover:bg-gray-800" data-testid="owner-logout">
              <LogOut className="h-3 w-3 mr-1" /> Sign out
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 -mt-3 pb-12 space-y-5">
        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Kpi label="Vehicles Listed" value={bookings.vehicles.length} color="blue" testid="kpi-vehicles" />
          <Kpi label="Upcoming Bookings" value={bookings.totals.upcoming || 0} color="amber" testid="kpi-upcoming" />
          <Kpi label="Pending Payout" value={f$(payouts.totals.pending)} color="amber" testid="kpi-pending" />
          <Kpi label="Paid Lifetime" value={f$(payouts.totals.paid)} color="emerald" testid="kpi-paid" />
        </div>

        {/* Vehicles */}
        <Card className="p-5" data-testid="vehicles-card">
          <h2 className="font-bold mb-3 flex items-center gap-2"><Car className="h-4 w-4 text-red-600" /> Your Vehicles on ACE</h2>
          {bookings.vehicles.length === 0 ? (
            <p className="text-xs text-gray-400 italic text-center py-8 border border-dashed rounded">No vehicles assigned yet. Our team will activate your fleet after onboarding.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {bookings.vehicles.map(v => (
                <div key={v.vehicle_pl_id} className="border border-gray-200 rounded-lg p-4 hover:border-gray-400">
                  <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{v.category || 'Vehicle'}</p>
                  <p className="font-bold mt-1">{v.vehicle_name}</p>
                  <p className="text-xs text-gray-500 mt-2">YTD revenue: <span className="font-semibold text-gray-900">{f$(v.total_revenue || 0)}</span></p>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Bookings */}
        <Card className="p-5" data-testid="bookings-card">
          <h2 className="font-bold mb-3 flex items-center gap-2"><Calendar className="h-4 w-4 text-blue-600" /> Recent Bookings</h2>
          {bookings.bookings.length === 0 ? (
            <p className="text-xs text-gray-400 italic text-center py-8 border border-dashed rounded">No bookings yet on your vehicles.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
                  <tr><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Vehicle</th><th className="text-left px-3 py-2">Client</th><th className="text-right px-3 py-2">Revenue</th><th className="text-center px-3 py-2">Status</th></tr>
                </thead>
                <tbody>
                  {bookings.bookings.slice(0, 15).map(b => (
                    <tr key={b.booking_id} className="border-t">
                      <td className="px-3 py-2 text-xs">{fDate(b.event_date)}</td>
                      <td className="px-3 py-2 text-xs">{b.vehicle || '—'}</td>
                      <td className="px-3 py-2 text-xs">{b.client_name || '—'}</td>
                      <td className="px-3 py-2 text-right font-semibold">{f$(b.total || 0)}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_STYLE[b.cc_status] || STATUS_STYLE.completed}`}>
                          {(b.cc_status || 'completed').toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Payouts */}
        <Card className="p-5" data-testid="payouts-card">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="font-bold flex items-center gap-2"><DollarSign className="h-4 w-4 text-green-700" /> Payouts</h2>
            <Button size="sm" variant="outline" onClick={() => setShowStatement(true)} data-testid="view-statement-btn">
              <FileText className="h-3 w-3 mr-1" /> View {statement?.year} Statement
            </Button>
          </div>
          {payouts.payouts.length === 0 ? (
            <p className="text-xs text-gray-400 italic text-center py-8 border border-dashed rounded">No payouts yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
                  <tr><th className="text-left px-3 py-2">Period</th><th className="text-right px-3 py-2">Gross</th><th className="text-right px-3 py-2">Expenses</th><th className="text-right px-3 py-2">Your Share</th><th className="text-right px-3 py-2">Payout</th><th className="text-center px-3 py-2">Status</th></tr>
                </thead>
                <tbody>
                  {payouts.payouts.map(p => (
                    <tr key={p.payout_id} className="border-t">
                      <td className="px-3 py-2 text-xs font-mono">{p.period_month}</td>
                      <td className="px-3 py-2 text-right">{f$(p.gross_revenue)}</td>
                      <td className="px-3 py-2 text-right text-red-700">{f$(p.expenses)}</td>
                      <td className="px-3 py-2 text-right">{p.revenue_share_percent}%</td>
                      <td className="px-3 py-2 text-right font-bold text-green-700">{f$(p.owner_payout)}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_STYLE[p.status] || STATUS_STYLE.pending}`}>
                          {(p.status || 'pending').toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Maintenance */}
        <Card className="p-5" data-testid="maintenance-card">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="font-bold flex items-center gap-2"><Wrench className="h-4 w-4 text-orange-600" /> Maintenance</h2>
            <p className="text-xs text-gray-500">YTD spend: <span className="font-bold text-gray-900">{f$(maintenance.ytd_spend)}</span></p>
          </div>
          {maintenance.schedules.length === 0 ? (
            <p className="text-xs text-gray-400 italic text-center py-6 border border-dashed rounded">No maintenance schedules yet.</p>
          ) : (
            <div className="space-y-2">
              {maintenance.schedules.map(s => (
                <div key={s.schedule_id} className="border rounded-lg p-3 flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-semibold text-sm">{s.service_type} <span className="text-xs text-gray-400 ml-2">{s.vehicle_name}</span></p>
                    <p className="text-[10px] text-gray-500 mt-0.5">Last: {fDate(s.last_done_at)} @ {Number(s.last_done_mileage || 0).toLocaleString()} mi</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_STYLE[s.status] || STATUS_STYLE.ok}`}>{(s.status || 'ok').toUpperCase()}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <p className="text-[11px] text-gray-400 text-center mt-2">
          Questions? Email <a href="mailto:owners@yourautoconcierge.com" className="text-red-600 hover:underline">owners@yourautoconcierge.com</a> · Statements above $600 trigger a 1099-NEC at year-end.
        </p>
      </div>

      {/* 1099-style statement modal */}
      {showStatement && statement && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 print:bg-white print:p-0" onClick={() => setShowStatement(false)}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[92vh] overflow-y-auto p-8 print:max-w-none print:rounded-none print:shadow-none" onClick={e => e.stopPropagation()} data-testid="statement-modal">
            <div className="flex items-center justify-between mb-5 print:hidden">
              <h3 className="text-lg font-bold flex items-center gap-2"><FileText className="h-5 w-5 text-red-600" /> {statement.year} Annual Statement</h3>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => window.print()}><Printer className="h-4 w-4" /></Button>
                <button onClick={() => setShowStatement(false)} className="text-2xl text-gray-400">×</button>
              </div>
            </div>
            <div className="border-b border-gray-200 pb-4 mb-4">
              <p className="text-[10px] uppercase tracking-widest text-red-600 font-bold">Annual Owner Statement</p>
              <h2 className="text-2xl font-black tracking-tight mt-1">{statement.owner?.owner_name}</h2>
              <p className="text-xs text-gray-500 mt-1">Tax year {statement.year} · TIN {statement.owner?.tin_masked || '—'} · Share {statement.owner?.revenue_share_percent}%</p>
              <p className="text-xs text-gray-500">{statement.owner?.address}</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              <SmallTile label="Gross" value={f$(statement.totals?.gross_revenue)} />
              <SmallTile label="Expenses" value={f$(statement.totals?.expenses)} accent="red" />
              <SmallTile label="Paid Out" value={f$(statement.totals?.owner_payout_paid)} accent="green" />
              <SmallTile label="Pending" value={f$(statement.totals?.owner_payout_pending)} accent="amber" />
            </div>
            {statement.totals?.ready_for_1099 && (
              <div className="bg-green-50 border border-green-200 rounded p-3 text-xs text-green-900 mb-4">
                <strong>1099-NEC will be issued.</strong> Total paid this year ({f$(statement.totals.owner_payout_paid)}) exceeds the $600 IRS threshold.
              </div>
            )}
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
                <tr><th className="text-left px-2 py-1.5">Period</th><th className="text-right px-2 py-1.5">Gross</th><th className="text-right px-2 py-1.5">Exp</th><th className="text-right px-2 py-1.5">Payout</th><th className="text-center px-2 py-1.5">Status</th></tr>
              </thead>
              <tbody>
                {(statement.payouts || []).map(p => (
                  <tr key={p.payout_id} className="border-t">
                    <td className="px-2 py-1.5 font-mono">{p.period_month}</td>
                    <td className="px-2 py-1.5 text-right">{f$(p.gross_revenue)}</td>
                    <td className="px-2 py-1.5 text-right text-red-700">{f$(p.expenses)}</td>
                    <td className="px-2 py-1.5 text-right font-bold">{f$(p.owner_payout)}</td>
                    <td className="px-2 py-1.5 text-center text-[10px]">{(p.status || 'pending').toUpperCase()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[10px] text-gray-400 mt-4 text-center print:mt-8">
              ACE Auto Concierge · Atlanta, GA · (678) 846-3009 · This statement is for your records.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

const Kpi = ({ label, value, color, testid }) => {
  const map = { red: 'border-red-200 text-red-700', amber: 'border-amber-200 text-amber-800', emerald: 'border-emerald-200 text-emerald-800', blue: 'border-blue-200 text-blue-700' };
  return (
    <Card className={`p-4 border ${map[color]}`} data-testid={testid}>
      <p className="text-[10px] font-bold uppercase tracking-widest opacity-70">{label}</p>
      <p className="text-2xl font-black mt-1">{value}</p>
    </Card>
  );
};

const SmallTile = ({ label, value, accent }) => {
  const map = { red: 'text-red-700', amber: 'text-amber-800', green: 'text-green-700' };
  return (
    <div className="bg-gray-50 border border-gray-200 rounded px-3 py-2">
      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</p>
      <p className={`text-base font-black mt-0.5 ${map[accent] || 'text-gray-900'}`}>{value}</p>
    </div>
  );
};

const Center = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center text-sm text-gray-500">{children}</div>
);
